// Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  UserRoundCog,
  Building2,
  X,
  ChevronDown,
  ChevronRight,
  LogOut
} from 'lucide-react';

export default function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Vérifier le mode sombre au chargement et quand il change
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    // Vérifier initial
    checkDarkMode();

    // Observer les changements de classe sur l'élément html
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const isActive = (path) => location.pathname === path;

  const navigation = [
    {
      name: 'Tableau de bord',
      path: '/dashboard',
      icon: LayoutDashboard,
      color: 'text-emerald-600 dark:text-emerald-400'
    },
    {
      name: 'Commandes',
      path: '/orders',
      icon: ShoppingCart,
      color: 'text-amber-600 dark:text-amber-400'
    },
    {
      name: 'Profil',
      path: '/account',
      icon: UserRoundCog,
      color: 'text-purple-600 dark:text-purple-400'
    }
  ];

  const toggleMenu = (index) => {
    setOpenMenus(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <>
      {/* Backdrop mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm min-md:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar container - Version avec style inline pour forcer le mode sombre */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-72 min-sm:w-80 
          transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          min-md:translate-x-0 min-md:relative min-md:shadow-none
        `}
        style={{
          backgroundColor: isDarkMode ? '#030712' : '#ffffff', // gray-950 en hex = #030712
          borderRight: `1px solid ${isDarkMode ? '#1f2937' : '#e5e7eb'}`, // gray-800 en dark, gray-200 en light
        }}
      >
        {/* Header / Logo */}
        <div 
          className="h-16 px-6 flex items-center justify-between border-b"
          style={{
            borderBottomColor: isDarkMode ? '#1f2937' : '#e5e7eb' // gray-800 en dark, gray-200 en light
          }}
        >
          <div className="flex items-center mx-auto gap-3">
            <img src="/img/logo.png" alt="" className='w-40 h-16' />
          </div>

          <button
            onClick={() => setSidebarOpen(false)}
            className="min-md:hidden p-2 rounded-lg"
            style={{
              color: isDarkMode ? '#d1d5db' : '#4b5563', // gray-300 en dark, gray-600 en light
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label="Fermer le menu"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 overflow-y-auto">
          <ul className="space-y-1.5">
            {navigation.map((item, index) => {
              const active = isActive(item.path);
              const hasSub = !!item.subItems;
              const isOpen = openMenus[index];

              return (
                <li key={item.name}>
                  {hasSub ? (
                    <>
                      <button
                        onClick={() => toggleMenu(index)}
                        className={`
                          w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium
                          transition-all duration-200
                        `}
                        style={{
                          backgroundColor: isOpen || active 
                            ? (isDarkMode ? '#052e16' : '#ecfdf5') // emerald-950/40 en dark, emerald-50 en light
                            : 'transparent',
                          color: isOpen || active
                            ? (isDarkMode ? '#6ee7b7' : '#047857') // emerald-300 en dark, emerald-700 en light
                            : (isDarkMode ? '#d1d5db' : '#374151') // gray-300 en dark, gray-700 en light
                        }}
                        onMouseEnter={(e) => {
                          if (!(isOpen || active)) {
                            e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!(isOpen || active)) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <item.icon 
                            className="h-5 w-5" 
                            style={{
                              color: active && !isDarkMode ? '#ffffff' : 
                                     isDarkMode ? '#34d399' : 
                                     item.color.includes('emerald') ? '#059669' :
                                     item.color.includes('amber') ? '#d97706' :
                                     item.color.includes('purple') ? '#7c3aed' : '#6b7280'
                            }}
                          />
                          <span>{item.name}</span>
                        </div>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {isOpen && (
                        <ul className="mt-1 ml-6 pl-4 space-y-1 animate-fade-in"
                          style={{
                            borderLeftColor: isDarkMode ? '#1f2937' : '#e5e7eb'
                          }}
                        >
                          {item.subItems.map(sub => (
                            <li key={sub.path}>
                              <Link
                                to={sub.path}
                                onClick={() => setSidebarOpen(false)}
                                className="flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-150"
                                style={{
                                  backgroundColor: isActive(sub.path)
                                    ? (isDarkMode ? '#052e16' : '#ecfdf5')
                                    : 'transparent',
                                  color: isActive(sub.path)
                                    ? (isDarkMode ? '#6ee7b7' : '#047857')
                                    : (isDarkMode ? '#9ca3af' : '#4b5563')
                                }}
                                onMouseEnter={(e) => {
                                  if (!isActive(sub.path)) {
                                    e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isActive(sub.path)) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                {sub.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200"
                      style={{
                        backgroundColor: active
                          ? '#059669' // emerald-600 fixe pour l'item actif
                          : 'transparent',
                        color: active
                          ? '#ffffff'
                          : (isDarkMode ? '#d1d5db' : '#374151')
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <item.icon 
                        className="h-5 w-5" 
                        style={{
                          color: active ? '#ffffff' : 
                                 isDarkMode ? '#34d399' : 
                                 item.color.includes('emerald') ? '#059669' :
                                 item.color.includes('amber') ? '#d97706' :
                                 item.color.includes('purple') ? '#7c3aed' : '#6b7280'
                        }}
                      />
                      <span>{item.name}</span>
                      {active && (
                        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/80" />
                      )}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t"
          style={{
            borderTopColor: isDarkMode ? '#1f2937' : '#e5e7eb'
          }}
        >
          <a
            href="/"
            className="flex items-center gap-2.5 text-sm font-medium transition-colors group"
            style={{
              color: isDarkMode ? '#9ca3af' : '#4b5563'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = isDarkMode ? '#34d399' : '#059669';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = isDarkMode ? '#9ca3af' : '#4b5563';
            }}
          >
            <Building2 className="h-4 w-4" />
            <span>Retour au site Speed Tunisia</span>
            <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-100 translate-x-[-4px] group-hover:translate-x-0 transition-all" />
          </a>
        </div>
        {/* Footer */}
        <div className="absolute bottom-0 left-6 right-6 p-6 border-t border-gray-200 dark:border-gray-800">
          <p className="mb-0 xs:text-center">
                Copyright © {new Date().getFullYear()} {' '}
                <a
                  aria-label="themezaa"
                  className="text-themecolor underline-none"
                  rel="noreferrer"
                  target="_blank"
                  href="#"
                >
                  Xylario
                </a>
              </p>
        </div>
      </aside>
    </>
  );
}